"use client";

import { useEffect, useSyncExternalStore } from "react";
import { LOOT_CACHE as MOCK } from "./mock-data";
import type { Instance, Item, LootCache, Pool } from "./types";
import { canonicalSlot } from "./bis-slots";
import { API_BASE } from "./api";
import { isHiddenInstance } from "./season-display";

// ── Server-export shape (mirror of the addon's bonusroll.export.v2/v3) ──
//
// The server enriches every item at upload time, so by the time the
// frontend sees this payload, name / iconUrl / itemClass / itemSubclass
// are already filled in AND eligibleSpecs has been narrowed to specs
// whose class wears the item's armor type. The frontend just renders.
//
// v3 adds richer fields (itemLevel, itemQuality, classID/subClassID,
// link) but they're all optional — v2 payloads still parse cleanly.

interface ExportItem {
  id: number;
  name?: string | null;
  icon?: number | null;
  iconName?: string | null;
  iconUrl?: string | null;
  itemClass?: string | null;
  itemSubclass?: string | null;
  quality?: string | null;
  rawSlot?: string | null;
  slot: string;
  eligibleSpecs: number[];
  bossName?: string | null;
  isToken?: boolean | null;
}

interface ExportPool {
  id: string;
  label: string;
  isRaid: boolean;
  instanceID: number;
  instanceName: string;
  bossName?: string | null;
  encounterID?: number | null;
  items: ExportItem[];
}

interface ExportInstance {
  id: number;
  name: string;
  isRaid: boolean;
  poolIDs: string[];
}

export interface LootExport {
  schema: "bonusroll.export.v2" | "bonusroll.export.v3";
  scope: "all-classes";
  tier: number;
  tierName?: string | null;
  builtAt?: number;
  exportedAt?: number;
  exportedBy?: string;
  instances: ExportInstance[];
  pools: Record<string, ExportPool>;
}

// ── Transform server export → frontend LootCache ──

export function transformExport(exp: LootExport): LootCache {
  const items: Record<number, Item> = {};
  const pools: Record<string, Pool> = {};
  const instances: Record<number, Instance> = {};

  for (const inst of exp.instances) {
    // Skip world-boss instances entirely — they don't accept bonus rolls.
    if (isHiddenInstance(inst.id)) continue;
    instances[inst.id] = {
      id: inst.id,
      name: inst.name,
      isRaid: inst.isRaid,
      poolIDs: inst.poolIDs,
    };
  }

  for (const [poolID, pool] of Object.entries(exp.pools)) {
    if (isHiddenInstance(pool.instanceID)) continue;
    const itemOrder: number[] = [];

    for (const ei of pool.items) {
      const slot = canonicalSlot(ei.slot);
      const id = ei.id;

      if (!items[id]) {
        items[id] = {
          id,
          name: ei.name && ei.name.trim() !== "" ? ei.name : `Item #${id}`,
          iconID: ei.icon ?? undefined,
          iconName: ei.iconName ?? undefined,
          iconUrl: ei.iconUrl ?? undefined,
          slot,
          // Bonus rolls always award Myth-track gear → epic quality.
          quality: "epic",
          // Heuristic: raid items are higher ilvl than M+ in v1.
          itemLevel: pool.isRaid ? 645 : 626,
          eligibleSpecs: ei.eligibleSpecs,
          bossName: ei.bossName ?? undefined,
          isToken: ei.isToken ?? false,
        };
      } else {
        // Same item across multiple pools — union eligibleSpecs.
        const existing = items[id];
        const merged = new Set([...existing.eligibleSpecs, ...ei.eligibleSpecs]);
        existing.eligibleSpecs = Array.from(merged).sort((a, b) => a - b);
        if (!existing.bossName && ei.bossName) existing.bossName = ei.bossName;
      }

      itemOrder.push(id);
    }

    pools[poolID] = {
      id: pool.id,
      label: pool.label,
      isRaid: pool.isRaid,
      instanceID: pool.instanceID,
      instanceName: pool.instanceName,
      bossName: pool.bossName ?? undefined,
      encounterID: pool.encounterID ?? undefined,
      itemOrder,
    };
  }

  return {
    tier: exp.tierName || `Tier ${exp.tier}`,
    classID: 0, // global, not class-scoped
    builtAt: exp.builtAt ? new Date(exp.builtAt * 1000).toISOString() : new Date().toISOString(),
    instances,
    pools,
    items,
  };
}

// ── Reactive cache with localStorage persistence ──
//
// The user's traffic NEVER triggers a Blizzard API call. The flow is:
//   1. Module load: try to read a persisted enriched cache from
//      localStorage. If found, that's `current` — zero network calls.
//   2. First-ever load (or after STALE_MS): GET /api/loot/cache once.
//      The server already pre-enriched everything at admin upload time.
//   3. Subsequent loads within FRESH_MS: zero network calls.
//   4. After FRESH_MS but before STALE_MS: serve cached, revalidate in
//      the background. If the server's builtAt matches, no replacement.
//
// All Blizzard calls happen exactly once per item, ever, on the server
// at upload time — the cached_items DB table guarantees that.

// v3: bumped after extending the backend's eligibleSpecs filter to drop
// shields/warglaives/wands/etc. for classes that can't equip them. Old
// cached payloads have over-broad eligibleSpecs that inflate poolSize.
const CACHE_KEY = "bonusroll:loot-cache:v3";
// 60s freshness so server-side cleanups (the admin can rewrite
// eligibleSpecs by re-running the cleanup endpoint) propagate to live
// users within a minute instead of an hour. The follow-up fetch is a
// 200 with full payload, but the builtAt-equality short-circuit at
// the loadFromServer level means the response gets discarded almost
// immediately if nothing actually changed — the only cost is one
// round-trip per minute of active use, which is negligible.
const FRESH_MS = 60 * 1000;               // 60s: skip the network entirely
const STALE_MS = 7 * 24 * 60 * 60 * 1000; // 7d: drop the cache, force refetch

interface PersistedShape {
  cache: LootCache;
  builtAt: string;
  fetchedAt: number;
}

function readPersisted(): PersistedShape | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedShape;
    if (!parsed?.cache?.items || !parsed.fetchedAt) return null;
    if (Date.now() - parsed.fetchedAt > STALE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writePersisted(cache: LootCache) {
  if (typeof window === "undefined") return;
  try {
    const blob: PersistedShape = {
      cache,
      builtAt: cache.builtAt,
      fetchedAt: Date.now(),
    };
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(blob));
  } catch {
    // Quota / private mode — silently ignore.
  }
}

const __initial = (() => {
  const p = readPersisted();
  return p ? { cache: p.cache, fetchedAt: p.fetchedAt } : { cache: MOCK, fetchedAt: 0 };
})();

let current: LootCache = __initial.cache;
let lastFetchedAt: number = __initial.fetchedAt;
let loadStarted = false;
let loadFailed = false;
const listeners = new Set<() => void>();

function emit() { for (const l of listeners) l(); }
function subscribe(cb: () => void) { listeners.add(cb); return () => listeners.delete(cb); }
function snapshot(): LootCache { return current; }
function serverSnapshot(): LootCache { return MOCK; }

function isFresh(): boolean {
  return lastFetchedAt > 0 && Date.now() - lastFetchedAt < FRESH_MS;
}

async function loadFromServer() {
  if (loadStarted) return;
  loadStarted = true;

  // Skip the network entirely if our persisted cache is fresh.
  if (current !== MOCK && isFresh()) return;

  try {
    // `cache: "no-store"` so the browser's HTTP cache doesn't serve a
    // stale Cache-Control:max-age payload after an admin save. The
    // `?_=` query param defeats CDN caching (CloudFront sees a unique
    // URL each call). Combined, an admin's invalidateLootCache() is
    // guaranteed to hit the origin and fetch the latest builtAt.
    const url = `${API_BASE}/api/loot/cache?_=${Date.now()}`;
    const resp = await fetch(url, {
      credentials: "include",
      cache: "no-store",
    });
    if (!resp.ok) {
      loadFailed = true;
      return;
    }
    const exp = (await resp.json()) as LootExport;
    if (!exp || (exp.schema !== "bonusroll.export.v2" && exp.schema !== "bonusroll.export.v3")) {
      loadFailed = true;
      return;
    }

    const serverBuiltAt = exp.builtAt
      ? new Date(exp.builtAt * 1000).toISOString()
      : "";

    // If the server's builtAt matches what we already have, nothing
    // changed — keep the existing copy and just bump the freshness
    // timestamp so we skip the next round-trip too.
    if (current !== MOCK && serverBuiltAt && current.builtAt === serverBuiltAt) {
      lastFetchedAt = Date.now();
      writePersisted(current);
      return;
    }

    // New cache from the server (already fully enriched + filtered).
    current = transformExport(exp);
    lastFetchedAt = Date.now();
    emit();
    writePersisted(current);
  } catch {
    loadFailed = true;
  }
}

/** Hook: returns the current loot cache (server-loaded if available, else mock). */
export function useLootCache(): LootCache {
  const cache = useSyncExternalStore(subscribe, snapshot, serverSnapshot);
  useEffect(() => { void loadFromServer(); }, []);
  return cache;
}

/** True once we've tried to load from server (success or failure). */
export function lootCacheLoadStatus(): { started: boolean; failed: boolean } {
  return { started: loadStarted, failed: loadFailed };
}

/** Power-user helper: clear the persisted cache and force a fresh fetch.
 *  Useful right after an admin uploads a new export. */
export function invalidateLootCache() {
  if (typeof window !== "undefined") {
    try { window.localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
  }
  current = MOCK;
  lastFetchedAt = 0;
  loadStarted = false;
  loadFailed = false;
  emit();
  void loadFromServer();
}

// Dev-only window helper: window.__bonusroll.{getCache,getStatus,invalidate}.
if (typeof window !== "undefined") {
  (window as unknown as { __bonusroll: unknown }).__bonusroll = {
    getCache: () => current,
    getStatus: () => ({
      started: loadStarted, failed: loadFailed,
      lastFetchedAt, fresh: isFresh(),
    }),
    invalidate: invalidateLootCache,
  };
}
